import { v } from "convex/values";
import {
  defineTableWithInternalControl,
  internalControl,
  validatorDisplaySortItem,
} from "../../lib/shared/validators";
import { defineTable } from "convex/server";
import { customFieldValues } from "../custom_fields";

// Re-export para retrocompat con consumidores que aún importan este symbol
// desde aquí. La definición canónica vive en `lib/shared/validators`.
export { validatorDisplaySortItem };

export const quotationStatus = v.union(
  v.literal("draft"),
  v.literal("sent"),
  v.literal("accepted"),
  v.literal("rejected"),
  v.literal("expired"),
);

export const QUOTATION_STATUSES = [
  "draft",
  "sent",

  "rejected",
  "expired",
  "accepted",
] as const;

export const validatorQuotation = v.object({
  sellerId: v.optional(v.id("sellers")),
  // clientId opcional: borradores se crean sin cliente (autosave lo completa)
  clientId: v.optional(v.id("clients")),
  workOrderId: v.optional(v.id("work_orders")),
  internalControl: internalControl,
  /** Valores de campos personalizados (Colecciones), por key de definición. */
  customFields: v.optional(customFieldValues),
  code: v.string(),
  title: v.optional(v.string()),
  description: v.optional(v.string()),
  status: v.optional(quotationStatus),
  version: v.number(),
  validUntil: v.optional(v.number()),
  exemptAmount: v.optional(v.number()),
  discountAmount: v.optional(v.number()),
  subtotal: v.optional(v.number()),
  taxAmount: v.optional(v.number()),
  totalAmount: v.optional(v.number()),
  currency: v.optional(v.string()),
  paymentTerms: v.optional(v.string()),
  notes: v.optional(v.string()),
  sentAt: v.optional(v.number()),
  viewedAt: v.optional(v.number()),
  acceptedAt: v.optional(v.number()),
  rejectedAt: v.optional(v.number()),
  // true mientras un borrador CRDT se está materializando: oculta la fila para evitar reflujo visible
  materializing: v.optional(v.boolean()),
  // Sort visual persistido en la BD. Lo escribe el cliente cuando el usuario
  // hace click-to-sort en un header; lo lee `getQuotationForPdf` para ordenar
  // los items antes de pasarlos al template. Si está ausente o vacío, los
  // items se ordenan por `sortOrder` (orden manual via drag-and-drop).
  displaySort: v.optional(v.array(validatorDisplaySortItem)),
});

// Payload de cabecera aceptado por el commit CRDT y `syncQuotation`.
// Excluye campos derivados y metadata del servidor; solo lo que viene del cliente.
export const validatorQuotationHeaderPayload = v.object({
  clientId: v.optional(v.id("clients")),
  sellerId: v.optional(v.id("sellers")),
  title: v.optional(v.string()),
  description: v.optional(v.string()),
  validUntil: v.optional(v.number()),
  currency: v.optional(v.string()),
  paymentTerms: v.optional(v.string()),
  notes: v.optional(v.string()),
  workOrderId: v.optional(v.id("work_orders")),
});

export const quotations = defineTable(validatorQuotation)
  .index("by_client_id", ["clientId"])
  .index("by_work_order_id", ["workOrderId"])
  .index("by_company_and_code", ["internalControl.companyId", "code"])
  .index("by_company_and_code_and_version", [
    "internalControl.companyId",
    "code",
    "version",
  ])
  .index("by_status", ["status"])
  .index("by_code", ["code"])
  .index("by_code_version", ["code", "version"])
  .index("by_company_and_status", ["internalControl.companyId", "status"])
  .index("by_branch_and_status", ["internalControl.branchId", "status"])
  .index("by_company_and_client", ["internalControl.companyId", "clientId"])
  .index("by_company_id", ["internalControl.companyId"])
  .index("by_branch_id", ["internalControl.branchId"])
  .index("by_state", ["internalControl.state"])
  .index("by_company_and_state", [
    "internalControl.companyId",
    "internalControl.state",
  ])
  .index("by_company_and_branch", [
    "internalControl.companyId",
    "internalControl.branchId",
  ])
  .index("by_branch_and_state", [
    "internalControl.branchId",
    "internalControl.state",
  ])
  .index("by_created_by", ["internalControl.createdBy"])
  .index("by_updated_by", ["internalControl.updatedBy"])
  .index("by_created_at", ["internalControl.createdAt"])
  .index("by_company_and_created_at", [
    "internalControl.companyId",
    "internalControl.createdAt",
  ])
  // Dashboard: rango de fechas dentro de una sucursal sin traer toda la sucursal.
  .index("by_company_and_branch_and_created_at", [
    "internalControl.companyId",
    "internalControl.branchId",
    "internalControl.createdAt",
  ]);
